import { useState, useEffect, useCallback, useMemo } from 'react'
import Editor from '@monaco-editor/react'
import { Button, Input, Textarea, Toggle, Tabs, Badge, Spinner, ConfirmDialog, Select, Modal } from '@/ui'
import { apiFetch } from '@/lib/api'
import { useMonacoModelCleanup } from '../../hooks/useMonacoModelCleanup'
import { useToast } from '../../components/ToastContainer'
import type { SectionDesign, Stylesheet } from './types'
import { toSlug } from './slug'
import { needsDataSectionHint, previewDisabledReason } from './preview'
import styles from '../SectionDesigns.module.css'

type CodeTab = 'html' | 'css' | 'js'

interface Props {
  section: SectionDesign | null // null = 새 섹션 생성 모드
  stylesheets: Stylesheet[]
  /** 미리보기에 적용할 전역 스타일시트 id. '' = 적용 안 함 */
  stylesheetId: string
  onStylesheetChange: (id: string) => void
  onSaved: () => void
  onDeleted: () => void
}

export function SectionDesignPanel({
  section,
  stylesheets,
  stylesheetId,
  onStylesheetChange,
  onSaved,
  onDeleted,
}: Props) {
  const { addToast } = useToast()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [sectionType, setSectionType] = useState('')
  const [tags, setTags] = useState('') // 쉼표 구분 입력
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [sortOrder, setSortOrder] = useState(0)
  const [html, setHtml] = useState('')
  const [css, setCss] = useState('')
  const [js, setJs] = useState('')
  const [tab, setTab] = useState<CodeTab>('html')
  const [saving, setSaving] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // 메타 정보(이름·slug·섹션타입·태그·설명·정렬·활성)는 편집 빈도가 낮아 모달로 분리.
  // 새 섹션은 필수값(이름·slug·섹션타입)을 먼저 받아야 하므로 열린 상태로 시작한다.
  const [showMeta, setShowMeta] = useState(!section)

  useEffect(() => {
    setName(section?.name ?? '')
    setSlug(section?.slug ?? '')
    setSlugTouched(!!section)
    setSectionType(section?.sectionType ?? '')
    setTags((section?.tags ?? []).join(', '))
    setDescription(section?.description ?? '')
    setIsActive(section?.isActive ?? true)
    setSortOrder(section?.sortOrder ?? 0)
    setHtml(section?.htmlContent ?? '')
    setCss(section?.cssContent ?? '')
    setJs(section?.jsContent ?? '')
    setTab('html')
    setShowMeta(!section)
  }, [section])

  // 새 섹션 생성 시 name 입력하면 slug 자동 생성 (사용자가 slug를 직접 건드리기 전까지)
  const handleNameChange = (v: string) => {
    setName(v)
    if (!slugTouched) setSlug(toSlug(v))
  }

  // ── 미리보기 (디바운스) ──
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  useEffect(() => {
    if (!html.trim()) {
      setPreviewHtml('')
      setPreviewError(null)
      setPreviewLoading(false)
      return
    }
    setPreviewLoading(true)
    const timer = setTimeout(async () => {
      try {
        // 문서 조립(전역 CSS → 스코프된 섹션 CSS → .wrapper 래핑)은 서버가 실제 발행
        // 렌더와 같은 순서로 수행한다 (apps/api/src/lib/templates/section-preview.ts).
        const res = await apiFetch<{ renderedHtml: string }>('/api/admin/section-designs/preview', {
          method: 'POST',
          body: JSON.stringify({
            htmlContent: html,
            cssContent: css || null,
            sectionType,
            stylesheetId: stylesheetId || null,
          }),
        })
        setPreviewHtml(res.renderedHtml)
        setPreviewError(null)
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Liquid 렌더링 실패'
        setPreviewError(message)
        setPreviewHtml('')
      } finally {
        setPreviewLoading(false)
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [html, css, sectionType, stylesheetId])

  const disabledReason = sectionType ? previewDisabledReason(sectionType, html) : null
  const scopeHint = needsDataSectionHint({ htmlContent: html, cssContent: css || null })

  const sheetOptions = useMemo(
    () => [
      { value: '', label: '전역 시트 없음' },
      ...stylesheets.map((s) => ({ value: s.id, label: s.name })),
    ],
    [stylesheets],
  )

  const handleSave = useCallback(async () => {
    if (!name.trim() || !slug.trim() || !sectionType.trim() || !html.trim()) {
      addToast({ type: 'error', message: '이름·slug·섹션타입·HTML은 필수입니다' })
      // 누락된 필수값이 메타 항목이면 모달을 열어 어디를 채워야 하는지 보여준다
      if (!name.trim() || !slug.trim() || !sectionType.trim()) setShowMeta(true)
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim(),
        sectionType: sectionType.trim(),
        htmlContent: html,
        cssContent: css.trim() ? css : null,
        jsContent: js.trim() ? js : null,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        description: description.trim() || null,
        isActive,
        sortOrder,
      }
      if (section) {
        await apiFetch(`/api/admin/section-designs/${section.id}`, { method: 'PUT', body: JSON.stringify(payload) })
      } else {
        await apiFetch('/api/admin/section-designs', { method: 'POST', body: JSON.stringify(payload) })
      }
      addToast({ type: 'success', message: '저장 완료' })
      onSaved()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '저장 실패'
      addToast({ type: 'error', message })
    } finally {
      setSaving(false)
    }
  }, [name, slug, sectionType, html, css, js, tags, description, isActive, sortOrder, section, addToast, onSaved])

  const handleDelete = useCallback(async () => {
    if (!section) return
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/section-designs/${section.id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: `"${section.name}" 삭제 완료` })
      setShowDelete(false)
      onDeleted()
    } catch (err: unknown) {
      // 409 IN_USE 메시지를 그대로 노출 (참조 카운트 포함)
      const message = err instanceof Error ? err.message : '삭제 실패'
      addToast({ type: 'error', message })
    } finally {
      setDeleting(false)
    }
  }, [section, addToast, onDeleted])

  const codeValue = tab === 'html' ? html : tab === 'css' ? css : js
  // HTML 탭은 Liquid 템플릿이라 'liquid' 모드로 본다 — basic-language(토크나이저 전용)라
  // 진단이 없고 {% %}·{{ | filter }} 하이라이트가 정확하다.
  const codeLang = tab === 'html' ? 'liquid' : tab === 'css' ? 'css' : 'javascript'
  // 탭마다 다른 path(=모델 URI)를 줘야 한다. path 를 안 주면 모든 탭이 같은 모델을
  // 공유해서, JS 탭에서 시작된 비동기 구문검증이 탭 전환 후 도착해 HTML 내용에
  // javascript 마커(허위 문법 오류)를 찍는다. 섹션 id 도 넣어 섹션 간 마커 누출을 막는다.
  const codeExt = tab === 'html' ? 'liquid' : tab === 'css' ? 'css' : 'js'
  const sectionKey = section?.id ?? 'new'
  const codePath = `section-design/${sectionKey}/${tab}.${codeExt}`
  useMonacoModelCleanup(`section-design/${sectionKey}/`)
  const setCode = (v: string | undefined) => {
    const val = v ?? ''
    if (tab === 'html') setHtml(val)
    else if (tab === 'css') setCss(val)
    else setJs(val)
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleGroup}>
          <h2 className={styles.panelTitle}>{name.trim() || (section ? '섹션 디자인 편집' : '새 섹션 디자인')}</h2>
          {sectionType && <Badge color="blue">{sectionType}</Badge>}
          {slug && <span className={styles.panelSlug}>{slug}</span>}
          {!isActive && <Badge color="gray">비활성</Badge>}
        </div>
        <div className={styles.panelActions}>
          <Button variant="outline" size="sm" onClick={() => setShowMeta(true)}>
            메타 정보
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </Button>
          {section && (
            <Button variant="outline" size="sm" onClick={() => setShowDelete(true)}>
              삭제
            </Button>
          )}
        </div>
      </div>

      {/* 에디터 + 미리보기 2분할 */}
      <div className={styles.editorPreviewSplit}>
        <div className={styles.editorCol}>
          <Tabs
            size="sm"
            items={[
              { key: 'html', label: 'HTML' },
              { key: 'css', label: 'CSS' },
              { key: 'js', label: 'JS' },
            ]}
            value={tab}
            onChange={(k) => setTab(k as CodeTab)}
          />
          {tab === 'css' && (
            <div className={styles.cssHint}>
              저장 시 <code>[data-section="X"]</code> 자동 스코프(S1) · <code>:root</code>·<code>@font-face</code>는
              전역 시트로
            </div>
          )}
          <div className={styles.editorArea}>
            <Editor
              height="100%"
              path={codePath}
              language={codeLang}
              value={codeValue}
              onChange={setCode}
              theme="vs-dark"
              options={{ minimap: { enabled: false }, fontSize: 13, wordWrap: 'on', tabSize: 2 }}
            />
          </div>
        </div>

        <div className={styles.previewCol}>
          <div className={styles.previewLabel}>
            <span className={styles.previewLabelText}>
              섹션 미리보기
              {disabledReason && <Badge color="yellow">비어보일 수 있음</Badge>}
            </span>
            <span className={styles.previewSheetSelect}>
              <Select
                size="sm"
                options={sheetOptions}
                value={stylesheetId}
                onChange={(e) => onStylesheetChange(e.target.value)}
                aria-label="미리보기에 적용할 전역 스타일시트"
              />
            </span>
          </div>
          {disabledReason && <div className={styles.previewNote}>{disabledReason}</div>}
          {scopeHint && (
            <div className={styles.previewNote}>
              HTML에 <code>data-section</code> 속성이 없어 섹션 CSS가 적용되지 않습니다 — 조립 시
              <code>[data-section=&quot;타입&quot;]</code>으로 스코프되므로 실제 발행에서도 동일합니다
            </div>
          )}
          <div className={styles.previewFrame}>
            {previewLoading ? (
              <div className={styles.previewCenter}>
                <Spinner /> 렌더링 중...
              </div>
            ) : previewError ? (
              <div className={styles.previewError}>
                <div className={styles.previewErrorTitle}>Liquid 렌더링 오류</div>
                <pre className={styles.previewErrorPre}>{previewError}</pre>
              </div>
            ) : previewHtml ? (
              <iframe srcDoc={previewHtml} title="섹션 미리보기" className={styles.previewIframe} />
            ) : (
              <div className={styles.previewCenter}>HTML을 입력하면 미리보기가 표시됩니다</div>
            )}
          </div>
        </div>
      </div>

      {/* 메타 정보 입력 모달 — 값은 패널 상태를 직접 편집하고, 실제 저장은 헤더의 [저장] 이 담당 */}
      <Modal
        open={showMeta}
        onClose={() => setShowMeta(false)}
        title={section ? '메타 정보' : '새 섹션 메타 정보'}
        size="lg"
      >
        <div className={styles.metaModalBody}>
          <div className={styles.metaModalGrid}>
            <Input
              label="이름"
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="t7 인사말"
            />
            <Input
              label="slug"
              required
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value)
                setSlugTouched(true)
              }}
              placeholder="t7-greeting"
              helperText="이름 입력 시 자동 생성됩니다"
            />
            <Input
              label="섹션 타입"
              required
              value={sectionType}
              onChange={(e) => setSectionType(e.target.value)}
              placeholder="greeting / gallery / cover ..."
              helperText="조립 시 슬롯 매칭과 CSS 스코프에 쓰입니다"
            />
            <Input
              label="태그 (쉼표 구분)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="t7, minimal"
            />
            <Input
              label="정렬 순서"
              type="number"
              value={String(sortOrder)}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            />
            {/* Toggle은 자체 <label>을 렌더링하므로 <div>로 감쌈 */}
            <div className={styles.metaModalToggle}>
              <Toggle checked={isActive} onChange={setIsActive} label="활성" size="sm" />
            </div>
          </div>
          <Textarea
            label="설명"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          <div className={styles.metaModalActions}>
            <Button variant="secondary" onClick={() => setShowMeta(false)}>
              닫기
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="섹션 디자인 삭제"
        message={`"${section?.name}" 섹션 디자인을 삭제하시겠습니까? 참조하는 템플릿이 있으면 삭제되지 않습니다.`}
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
      />
    </div>
  )
}
